import * as React from "react";
import { observer } from "mobx-react-lite";
import { Form, Input, InputNumber, Select, DatePicker, Switch } from "antd";
import { ReportParameter } from "../types";
import dayjs, { Dayjs } from "dayjs";
import ReportsStore from "../stores/ReportsStore";
import { Utils } from "../../common/misc/Utils";
import AppModal from "../../common/components/AppModal";

type Props = {
    store: ReportsStore;
    isVisible: boolean;
    parameters: ReportParameter[];
    onCancel: () => void;
    title?: string;
};

const PROJECT_PARAMETER_ID = "ProjectId";

const ReportParametersDialog: React.FC<Props> = ({
    store,
    isVisible,
    parameters,
    onCancel,
    title = "Report parameters",
}) => {
    const [form] = Form.useForm();

    const handleSubmit = () => {
        form.validateFields()
            .then((values) => {
                // Transform values to match parameter types
                const transformedValues: Record<string, unknown> = {};
                parameters.forEach((param) => {
                    const value = values[param.name];
                    if (value !== undefined && value !== null) {
                        // Convert dates to ISO strings if needed
                        if (
                            param.type === "System.DateTime" &&
                            dayjs.isDayjs(value)
                        ) {
                            // Use removeLocalTimeZone to ignore timezone
                            transformedValues[param.name] =
                                Utils.removeLocalTimeZone(value as Dayjs);
                        } else {
                            transformedValues[param.name] = value;
                        }
                    } else if (!param.allowNull) {
                        transformedValues[param.name] = value;
                    }
                });
                store.setParamereterToSubmit(transformedValues);
                handleClose();
            })
            .catch((error) => {
                console.error("Validation failed:", error);
            });
    };

    const handleClose = () => {
        form.resetFields();
        onCancel();
    };

    const renderParameterControl = (param: ReportParameter) => {
        // Get initial value from store.parametersToSubmit first, then fall back to param.value
        const storedValue = store.parametersToSubmit?.[param.name];
        const initialValue =
            storedValue !== undefined && storedValue !== null
                ? param.type === "System.DateTime"
                    ? dayjs(storedValue as string)
                    : storedValue
                : param.value
                  ? param.type === "System.DateTime"
                      ? dayjs(param.value as string)
                      : param.value
                  : undefined;

        // Build validation rules
        const rules = [];
        if (!param.allowNull) {
            rules.push({
                required: true,
                message: `${param.text} is required`,
            });
        }

        switch (param.type) {
            case "System.String":
                if (param.availableValues && param.availableValues.length > 0) {
                    // Dropdown for string with available values
                    return (
                        <Form.Item
                            key={param.name}
                            name={param.name}
                            label={
                                <span className="dialog-field-label">
                                    {param.text}
                                </span>
                            }
                            initialValue={initialValue}
                            rules={rules}
                            className="dialog-field"
                            colon={false}
                        >
                            <Select
                                placeholder={`Select ${param.text}`}
                                allowClear={param.allowNull}
                                options={param.availableValues.map((av) => ({
                                    label: av.label,
                                    value: av.value,
                                }))}
                            />
                        </Form.Item>
                    );
                } else {
                    // Text input for regular strings
                    return (
                        <Form.Item
                            key={param.name}
                            name={param.name}
                            label={
                                <span className="dialog-field-label">
                                    {param.text}
                                </span>
                            }
                            initialValue={initialValue}
                            rules={rules}
                            className="dialog-field"
                            colon={false}
                        >
                            <Input
                                placeholder={`Enter ${param.text}`}
                                allowClear
                            />
                        </Form.Item>
                    );
                }

            case "System.Int32":
            case "System.Decimal":
                return (
                    <Form.Item
                        key={param.name}
                        name={param.name}
                        label={
                            <span className="dialog-field-label">
                                {param.text}
                            </span>
                        }
                        initialValue={initialValue}
                        rules={rules}
                        className="dialog-field"
                        colon={false}
                    >
                        <InputNumber
                            placeholder={`Enter ${param.text}`}
                            style={{ width: "100%" }}
                            precision={param.type === "System.Decimal" ? 2 : 0}
                        />
                    </Form.Item>
                );

            case "System.Boolean":
                return (
                    <Form.Item
                        key={param.name}
                        name={param.name}
                        label={
                            <span className="dialog-field-label">
                                {param.text}
                            </span>
                        }
                        initialValue={initialValue ?? false}
                        valuePropName="checked"
                        className="dialog-field"
                        colon={false}
                    >
                        <Switch />
                    </Form.Item>
                );

            case "System.DateTime":
                return (
                    <Form.Item
                        key={param.name}
                        name={param.name}
                        label={
                            <span className="dialog-field-label">
                                {param.text}
                            </span>
                        }
                        initialValue={initialValue}
                        rules={rules}
                        className="dialog-field"
                        colon={false}
                    >
                        <DatePicker
                            placeholder={`Select ${param.text}`}
                            style={{ width: "100%" }}
                            format="YYYY-MM-DD"
                        />
                    </Form.Item>
                );

            default:
                return (
                    <Form.Item
                        key={param.name}
                        name={param.name}
                        label={
                            <span className="dialog-field-label">
                                {param.text}
                            </span>
                        }
                        initialValue={initialValue}
                        rules={rules}
                        className="dialog-field"
                        colon={false}
                    >
                        <Input placeholder={`Enter ${param.text}`} allowClear />
                    </Form.Item>
                );
        }
    };

    return (
        <AppModal
            title={title}
            open={isVisible}
            className="alpha-modal"
            closable={false}
            width={600}
            cancelButtonTitle="Cancel"
            onCancelClick={handleClose}
            cancelButtonProps={{
                "data-id": "report-parameters-cancel",
                className: "light alpha-dialog-button",
                size: undefined,
            }}
            submitButtonTitle="Save"
            submitButtonProps={{
                "data-id": "report-parameters-submit",
                className: "alpha-dialog-button",
                form: "report-parameters-form",
                size: undefined,
            }}
        >
            <Form
                form={form}
                onFinish={handleSubmit}
                className="alpha-form"
                layout="vertical"
                id="report-parameters-form"
            >
                {parameters
                    .filter((p) => p.id !== PROJECT_PARAMETER_ID)
                    .map((param) => renderParameterControl(param))}
            </Form>
        </AppModal>
    );
};

export default observer(ReportParametersDialog);
